import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PortfolioGame.css';
import terminalImage from '../assets/images/image-terminal.png';

// Platform configuration
const PLATFORMS = [
    { x: 100, y: 480, width: 300, height: 15, type: 'about' },
    { x: 450, y: 430, width: 200, height: 10 },
    { x: 700, y: 380, width: 150, height: 10, moving: { range: 100, speed: 50, vertical: true } },
    { x: 900, y: 340, width: 120, height: 10 },
    { x: 1100, y: 290, width: 120, height: 10 },
    { x: 1400, y: 320, width: 300, height: 15, type: 'projects' },
    { x: 1800, y: 270, width: 100, height: 10 },
    { x: 2000, y: 220, width: 100, height: 10 },
    { x: 2200, y: 170, width: 80, height: 10 },
    { x: 2400, y: 220, width: 120, height: 10 },
    { x: 2600, y: 270, width: 120, height: 10 },
    { x: 2300, y: 430, width: 250, height: 10 },
    { x: 2700, y: 480, width: 750, height: 15, type: 'experience' },
    { x: 3400, y: 440, width: 300, height: 15, type: 'contact' },
    { x: 3550, y: 390, width: 120, height: 10 },
    { x: 3700, y: 340, width: 120, height: 10 },
    { x: 3850, y: 290, width: 120, height: 15 },
    { x: 4000, y: 250, width: 80, height: 10 },
    { x: 4200, y: 220, width: 100, height: 10 },
    { x: 4400, y: 200, width: 300, height: 15, type: 'skills' },
    { x: 4800, y: 480, width: 300, height: 15 },
    { x: 5100, y: 480, width: 130, height: 15, type: 'fortress-altar' },
    { x: 5240, y: 480, width: 80, height: 15 },
    { x: 5270, y: 432, width: 20, height: 48, type: 'fortress-gate' },
];

const TERMINALS = [
    { x: 315, y: 430, type: 'about' },
    { x: 1430, y: 310, type: 'projects' },
    { x: 2930, y: 430, type: 'experience' },
    { x: 3450, y: 430, type: 'contact' },
    { x: 4600, y: 190, type: 'skills' },
];

const COLLECTIBLES = [
    { x: 250, y: 430, type: 'data' },
    { x: 600, y: 380, type: 'data' },
    { x: 1000, y: 280, type: 'data' },
    { x: 1500, y: 270, type: 'data' },
    { x: 2250, y: 130, type: 'key' },
    { x: 2500, y: 380, type: 'data' },
    { x: 3600, y: 330, type: 'data' },
    { x: 3800, y: 240, type: 'data' },
    { x: 4100, y: 200, type: 'data' },
    { x: 4400, y: 150, type: 'data' },
];

const DECORATIONS = [
    { x: 150, y: 450, type: 'holo-sign', text: 'Start', hidden: true },
    { x: 300, y: 440, type: 'holo-sign', text: 'About Me', hidden: true },
    { x: 1450, y: 280, type: 'holo-sign', text: 'Projects', hidden: true },
    { x: 2980, y: 440, type: 'holo-sign', text: 'Experience', hidden: true },
    { x: 3600, y: 400, type: 'holo-sign', text: 'Contact', hidden: true },
    { x: 4550, y: 170, type: 'holo-sign', text: 'Skills', hidden: true },
    { x: 5130, y: 450, type: 'holo-sign', text: 'Data Altar', hidden: false },
];

const SECTION_INFO = {
    about: {
        title: 'About Me Section',
        description: 'Accessing personal profile... I\'m Vishnu, a Computer Engineering student at the University of Waterloo passionate about robotics and embedded systems. I love creating solutions through engineering and programming. When not coding, I enjoy playing piano, chess, and reading web novels.',
        visitText: 'Visit About Page'
    },
    projects: {
        title: 'Projects Gallery',
        description: 'Scanning project repository... Discover my portfolio of innovative solutions including a Custom Dancing Robot with Arduino integration, STM32 ECG Signal Transmission system, Data Analysis projects using Python, and Computer Vision applications with OpenCV and PyTorch. Each project showcases different technical skills and problem-solving approaches.',
        visitText: 'View Projects'
    },
    experience: {
        title: 'Professional Experience',
        description: 'Loading career data... My professional journey includes working as a Web Developer at Samskrita Bharati, where I built an OCR service from scratch, and various leadership roles at Fletcher\'s Meadow Secondary School including President of the STEAM Club, Computer Club, and Brain Bee Club. These experiences demonstrate my technical skills and leadership abilities.',
        visitText: 'See Experience'
    },
    contact: {
        title: 'Contact Information',
        description: 'Establishing connection... Want to collaborate or discuss opportunities? You can reach out via email at vajit@uwaterloo.ca or connect with me on LinkedIn. I\'m always open to discussing new projects, opportunities, or just chatting about technology and engineering.',
        visitText: 'Contact Me'
    },
    skills: {
        title: 'Technical Skills',
        description: 'Processing skill database... My technical toolkit includes programming languages (C++, Python, JavaScript), web technologies (React.js, Node.js), hardware skills (PCB Design, Arduino, STM32), and data analysis tools (Pandas, NumPy, TensorFlow). I also bring soft skills in team leadership, project management, and problem-solving.',
        visitText: 'View Skills'
    },
    'fortress-altar': {
        title: 'Portfolio Overview',
        description: hasKey => hasKey 
            ? 'All sections of the portfolio have been previewed. The comprehensive view of my work, skills, and experience is now accessible. The gate to the complete portfolio has been opened!' 
            : 'To fully activate the portfolio overview, you need to collect the data key and visit more sections of the site.',
        visitText: 'Full Portfolio'
    },
    'fortress-gate': {
        title: 'Portfolio Complete',
        description: 'You\'ve explored all sections of my portfolio! Thank you for taking the time to learn about my work and skills through this interactive experience.',
        visitText: 'View Portfolio'
    }
};

// Add these constants for character customization
const CHARACTER_UPGRADES = [
    {
        id: 'blue_trail',
        name: 'Blue Energy Trail',
        description: 'Adds a glowing blue trail behind your character',
        cost: 30,
        type: 'effect'
    },
    {
        id: 'double_jump',
        name: 'Double Jump',
        description: 'Press jump twice to perform a double jump',
        cost: 50,
        type: 'ability'
    },
    {
        id: 'speed_boost',
        name: 'Speed Boost',
        description: 'Move 25% faster across platforms',
        cost: 40,
        type: 'stat'
    },
    {
        id: 'golden_glow',
        name: 'Golden Aura',
        description: 'Surround your character with a golden aura',
        cost: 30,
        type: 'effect'
    },
    {
        id: 'magnet',
        name: 'Data Magnet',
        description: 'Automatically collect data points when nearby',
        cost: 60,
        type: 'ability'
    }
];

const PortfolioGame = ({ onComplete, onSkip, onSectionUnlock }) => {
    const navigate = useNavigate();
    const [showInstructions, setShowInstructions] = useState(true);
    const [gameOver, setGameOver] = useState(false);
    const [paused, setPaused] = useState(false);
    const [muted, setMuted] = useState(false);
    const [score, setScore] = useState(0);
    const [terminalsActivated, setTerminalsActivated] = useState([]);
    const [hasKey, setHasKey] = useState(false);
    const [sectionInfo, setSectionInfo] = useState(null);
    const [checkpoint, setCheckpoint] = useState({ x: 150, y: 432 });
    const [player, setPlayer] = useState({
        x: 150,
        y: 432,
        width: 40,
        height: 48,
        velocityX: 0,
        velocityY: 0,
        isJumping: false,
        isMoving: false,
        facingRight: true,
        speed: 7,
        jumpPower: 14
    });
    const [cameraOffset, setCameraOffset] = useState(0);
    const [cameraOffsetY, setCameraOffsetY] = useState(0);
    const [keys, setKeys] = useState({ left: false, right: false, up: false, interact: false });
    const [collectibles, setCollectibles] = useState(COLLECTIBLES);
    const [platformStates, setPlatformStates] = useState(
        PLATFORMS.filter(p => p.moving).map(p => ({
            x: p.x,
            y: p.y,
            direction: 1
        }))
    );
    const [showShop, setShowShop] = useState(false);
    const [upgrades, setUpgrades] = useState([]);
    const [particles, setParticles] = useState([]);
    const [canDoubleJump, setCanDoubleJump] = useState(false);
    const [hasJumped, setHasJumped] = useState(false);
    const [magnetRange, setMagnetRange] = useState(0);

    const gameAreaRef = useRef(null);
    const gameAreaFocusRef = useRef(null);
    const animationRef = useRef(null);
    const lastUpdateTimeRef = useRef(null);

    // Audio handling with single instances
    const audioRefs = useRef({
        jump: null,
        collect: null,
        key: null,
        terminal: null,
        altar: null,
        fall: null,
        finish: null
    });

    // Initialize audio only once on component mount
    useEffect(() => {
        audioRefs.current = {
            jump: new Audio('/sounds/jump.mp3'),
            collect: new Audio('/sounds/collect.mp3'),
            key: new Audio('/sounds/key.mp3'),
            terminal: new Audio('/sounds/terminal.mp3'),
            altar: new Audio('/sounds/gate.mp3'),
            fall: new Audio('/sounds/fall.mp3'),
            finish: new Audio('/sounds/finish.mp3')
        };

        // Set initial volume for all audio files
        Object.values(audioRefs.current).forEach(audio => {
            audio.volume = muted ? 0 : 0.5;
            audio.preload = 'auto';
        });

        // Cleanup on unmount
        return () => {
            Object.values(audioRefs.current).forEach(audio => {
                audio.pause();
                audio.src = ''; // Clear the source to release resources
            });
        };
    }, [muted]);

    // Update volume when muted state changes
    useEffect(() => {
        Object.values(audioRefs.current).forEach(audio => {
            audio.volume = muted ? 0 : 0.5;
        });
    }, [muted]);

    const playSound = useCallback((type) => {
        if (muted) return;
        const audio = audioRefs.current[type];
        if (audio) {
            audio.currentTime = 0; // Reset to start
            audio.play().catch(err => console.error(`Failed to play sound ${type}:`, err));
        }
    }, [muted]);

    const updatePlatforms = useCallback((deltaTime) => {
        setPlatformStates(prev => prev.map((state, index) => {
            const platform = PLATFORMS.filter(p => p.moving)[index];
            let { y, direction } = state;
            const { range, speed, vertical } = platform.moving;
            y += direction * speed * deltaTime;
            if (vertical) {
                if (y > platform.y + range || y < platform.y - range) {
                    direction *= -1;
                    y = y > platform.y ? platform.y + range : platform.y - range;
                }
            }
            return { ...state, y, direction };
        }));
    }, []);

    const updatePlayer = useCallback((deltaTime) => {
        setPlayer(prev => {
            let velocityY = prev.velocityY + 50 * deltaTime;
            if (velocityY > 30) velocityY = 30;

            let velocityX = 0;
            let facingRight = prev.facingRight;
            let isMoving = false;

            // Horizontal movement - reset to 0 if no keys are pressed
            if (keys.left) {
                velocityX = -prev.speed;
                facingRight = false;
                isMoving = true;
            } else if (keys.right) {
                velocityX = prev.speed;
                facingRight = true;
                isMoving = true;
            }

            // Jump - apply once when key is pressed and player is on platform
            let isJumping = prev.isJumping;
            if (keys.up) {
                if (!prev.isJumping) {
                    velocityY = -prev.jumpPower;
                    isJumping = true;
                    setHasJumped(false);
                    playSound('jump');
                } 
                // Double jump logic - only allow if we have the upgrade and haven't used double jump yet
                else if (canDoubleJump && !hasJumped && velocityY > -5) {
                    velocityY = -prev.jumpPower * 0.8;
                    setHasJumped(true);
                    playSound('jump');
                }
            }

            let newX = prev.x + velocityX;
            let newY = prev.y + velocityY;

            const allPlatforms = PLATFORMS.map(p => {
                if (p.moving) {
                    const state = platformStates[PLATFORMS.filter(plat => plat.moving).indexOf(p)];
                    return { ...p, y: state.y };
                }
                return p;
            });

            const altarPlatformDetails = allPlatforms.find(p => p.type === 'fortress-altar');
            let altarCollision = false;
            if (altarPlatformDetails) {
                altarCollision = newX + prev.width > altarPlatformDetails.x &&
                                 newX < altarPlatformDetails.x + altarPlatformDetails.width &&
                                 prev.y + prev.height >= altarPlatformDetails.y &&
                                 prev.y < altarPlatformDetails.y + altarPlatformDetails.height;
            }

            if (altarCollision && !hasKey && keys.interact) {
                setSectionInfo({
                    title: 'Key Required',
                    description: 'You need to find the data key first before you can activate the altar.',
                });
            } else if (altarCollision && hasKey && !terminalsActivated.includes('fortress-altar') && keys.interact) {
                setTerminalsActivated(prevTerminals => [...prevTerminals, 'fortress-altar']);
                try {
                    setSectionInfo({
                        title: SECTION_INFO['fortress-altar'].title,
                        description: SECTION_INFO['fortress-altar'].description(hasKey),
                        visitText: SECTION_INFO['fortress-altar'].visitText,
                        visitPath: '/'
                    });
                } catch (e) {
                    console.error('Error setting altar section info:', e);
                }
                if (altarPlatformDetails) {
                    setCheckpoint({ 
                        x: altarPlatformDetails.x + (altarPlatformDetails.width / 2) - (prev.width / 2), 
                        y: altarPlatformDetails.y - prev.height 
                    });
                }
                playSound('altar');
            }

            const gateCollision = allPlatforms.find(p =>
                p.type === 'fortress-gate' &&
                newX + prev.width > p.x &&
                newX < p.x + p.width &&
                prev.y + prev.height > p.y &&
                prev.y < p.y + p.height &&
                !terminalsActivated.includes('fortress-altar')
            );

            if (gateCollision) {
                if (velocityX > 0) newX = gateCollision.x - prev.width;
                else if (velocityX < 0) newX = gateCollision.x + gateCollision.width;
                velocityX = 0;

                if (keys.interact && !terminalsActivated.includes('fortress-altar')) {
                    setSectionInfo({
                        title: 'Gate Locked',
                        description: 'You need to place the key on the altar first to unlock the gate.'
                    });
                }
            }

            const finishCollision = allPlatforms.find(p =>
                p.type === 'fortress-gate' &&
                newX + prev.width > p.x &&
                newX < p.x + p.width &&
                prev.y + prev.height > p.y &&
                prev.y < p.y + p.height &&
                terminalsActivated.includes('fortress-altar')
            );

            if (finishCollision && keys.interact) {
                setGameOver(true);
                setSectionInfo({
                    title: SECTION_INFO['fortress-gate'].title,
                    description: SECTION_INFO['fortress-gate'].description,
                    visitText: SECTION_INFO['fortress-gate'].visitText,
                    visitPath: '/'
                });
                onComplete && onComplete();
                playSound('finish');
            }

            const onPlatform = allPlatforms.find(p => {
                const playerBottom = prev.y + prev.height;
                const playerFeet = playerBottom + velocityY;
                return (
                    playerFeet >= p.y &&
                    playerBottom <= p.y + 2 &&
                    newX + prev.width > p.x + 5 &&
                    newX < p.x + p.width - 5 &&
                    velocityY >= 0
                );
            });

            if (onPlatform) {
                const fallAudio = audioRefs.current.fall;
                if (fallAudio && !fallAudio.paused) {
                    fallAudio.pause();
                    fallAudio.currentTime = 0;
                }
                velocityY = 0;
                newY = onPlatform.y - prev.height;
                isJumping = false;
            }

            if (newX < 0) newX = 0;
            if (newX > 5500) newX = 5500;
            if (newY > 600) {
                newX = checkpoint.x;
                newY = checkpoint.y;
                velocityY = 0;
                playSound('fall');
            }

            return { ...prev, x: newX, y: newY, velocityX, velocityY, isJumping, isMoving, facingRight };
        });
    }, [keys, platformStates, hasKey, terminalsActivated, checkpoint, onComplete, playSound, audioRefs, canDoubleJump, hasJumped]);

    const updateCamera = useCallback(() => {
        const gameWidth = gameAreaRef.current?.clientWidth || 800;
        const gameHeight = gameAreaRef.current?.clientHeight || 600;
        const targetOffsetX = player.x - gameWidth / 2 + player.width / 2;
        const targetOffsetY = player.y - gameHeight / 2 + player.height / 2;
        const clampedOffsetX = Math.max(0, Math.min(5500 - gameWidth, targetOffsetX));
        const clampedOffsetY = Math.max(0, Math.min(600 - gameHeight, targetOffsetY));
        setCameraOffset(prev => prev + 0.1 * (clampedOffsetX - prev));
        setCameraOffsetY(prev => prev + 0.1 * (clampedOffsetY - prev));
    }, [player.x, player.y, player.width, player.height]);

    const checkCollectibles = useCallback(() => {
        setCollectibles(prev => {
            const newCollectibles = prev.filter(item => {
                // Normal collision detection
                const collected =
                    player.x < item.x + 30 &&
                    player.x + player.width > item.x &&
                    player.y < item.y + 30 &&
                    player.y + player.height > item.y;
                
                // Magnet collision detection (only for data items, not keys)
                const magnetCollected = 
                    magnetRange > 0 && 
                    item.type === 'data' && 
                    Math.hypot(player.x + player.width/2 - item.x, player.y + player.height/2 - item.y) < magnetRange;
                
                if (collected || magnetCollected) {
                    // If magnet collection, add a particle effect moving toward player
                    if (magnetCollected && !collected) {
                        setParticles(prev => [...prev, {
                            id: Date.now() + Math.random(),
                            x: item.x,
                            y: item.y,
                            targetX: player.x + player.width/2,
                            targetY: player.y + player.height/2,
                            progress: 0
                        }]);
                    }
                
                    setScore(s => s + 10);
                    if (item.type === 'key') {
                        setHasKey(true);
                        setSectionInfo({
                            title: 'Data Key Acquired',
                            description: 'Key retrieved. Use it to activate the fortress altar and unlock the complete portfolio overview.'
                        });
                        playSound('key');
                    } else {
                        playSound('collect');
                    }
                    return false;
                }
                return true;
            });

            return newCollectibles;
        });
    }, [player, playSound, magnetRange]);

    const checkTerminals = useCallback(() => {
        TERMINALS.forEach(terminal => {
            if (
                !terminalsActivated.includes(terminal.type) &&
                player.x < terminal.x + 70 &&
                player.x + player.width > terminal.x - 70 &&
                player.y < terminal.y + 80 &&
                player.y + player.height > terminal.y - 27.5 &&
                keys.interact
            ) {
                setTerminalsActivated(prev => [...prev, terminal.type]);
                const platform = PLATFORMS.find(p => p.type === terminal.type);
                setCheckpoint({ x: terminal.x, y: platform ? platform.y - player.height : terminal.y - player.height });
                
                setSectionInfo({
                    ...SECTION_INFO[terminal.type],
                    visitPath: getVisitPath(terminal.type)
                });
                
                onSectionUnlock && onSectionUnlock(terminal.type);
                playSound('terminal');
            }
        });
    }, [player, keys.interact, terminalsActivated, onSectionUnlock, playSound]);
    
    const getVisitPath = (sectionType) => {
        switch(sectionType) {
            case 'about':
                return '/about';
            case 'projects':
                return '/#projects';
            case 'experience':
                return '/#experience'; // This path is correct, but we need to fix the handleVisit function
            case 'contact':
                return '/contact';
            case 'skills':
                return '/'; // Just bring to top of homepage
            default:
                return '/';
        }
    };

    const updateParticles = useCallback(() => {
        setParticles(prev => 
            prev
                .map(p => ({
                    ...p,
                    progress: p.progress + 0.05,
                    x: p.x + (p.targetX - p.x) * 0.1,
                    y: p.y + (p.targetY - p.y) * 0.1
                }))
                .filter(p => p.progress < 1)
        );
    }, []);

    const gameLoop = useCallback((timestamp) => {
        if (gameOver || paused) {
            animationRef.current = requestAnimationFrame(gameLoop);
            return;
        }

        const deltaTime = lastUpdateTimeRef.current ? (timestamp - lastUpdateTimeRef.current) / 1000 : 0;
        lastUpdateTimeRef.current = timestamp;

        updatePlayer(deltaTime);
        updatePlatforms(deltaTime);
        updateCamera();
        checkCollectibles();
        checkTerminals();
        updateParticles();
        
        animationRef.current = requestAnimationFrame(gameLoop);
    }, [updatePlayer, updatePlatforms, updateCamera, checkCollectibles, checkTerminals, gameOver, paused, updateParticles]);

    const toggleShop = () => {
        setPaused(true);
        setShowShop(true);
    };

    const purchaseUpgrade = (upgradeId) => {
        const upgrade = CHARACTER_UPGRADES.find(u => u.id === upgradeId);
        
        if (!upgrade || score < upgrade.cost || upgrades.includes(upgradeId)) {
            return;
        }
        
        // Deduct points
        setScore(prev => prev - upgrade.cost);
        
        // Add the upgrade
        setUpgrades(prev => [...prev, upgradeId]);
        
        // Apply upgrade effects
        applyUpgradeEffects(upgradeId);
        
        // Play purchase sound
        playSound('collect');
    };

    const applyUpgradeEffects = (upgradeId) => {
        switch(upgradeId) {
            case 'double_jump':
                setCanDoubleJump(true);
                break;
            case 'speed_boost':
                setPlayer(prev => ({
                    ...prev,
                    speed: prev.speed * 1.25
                }));
                break;
            case 'magnet':
                setMagnetRange(150);
                break;
            default:
                break;
        }
    };

    // Handle mobile/touch controls
    const handleButton = (action, pressed) => {
        switch (action) {
            case 'left':
                setKeys(prev => ({ ...prev, left: pressed }));
                break;
            case 'right':
                setKeys(prev => ({ ...prev, right: pressed }));
                break;
            case 'jump':
                setKeys(prev => ({ ...prev, up: pressed }));
                break;
            case 'interact':
                setKeys(prev => ({ ...prev, interact: pressed }));
                break;
            default:
                break;
        }
    };

    // Handle navigation to different sections
    const handleVisit = (path) => {
        onSkip();
        setTimeout(() => {
            // For anchor links (/#experience, /#projects, etc.)
            if (path.startsWith('/#')) {
                // Always navigate to home first
                navigate('/');
                const section = path.slice(2);
                // Give React Router time to render Home
                setTimeout(() => {
                    // Try finding by ID first
                    let el = document.getElementById(section);
                    
                    // If ID not found, try finding by class name
                    if (!el) {
                        el = document.querySelector(`.${section}-section`);
                    }
                    
                    if (el) {
                        const headerOffset = 120; // Add more offset to position it higher
                        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
                        window.scrollTo({
                            top: elementPosition - headerOffset,
                            behavior: 'smooth'
                        });
                    }
                }, 300);
            }
            else if (path === '/') {
                // Top of homepage
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            else {
                // Normal pages like /about or /contact
                navigate(path);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 100);
    };

    useEffect(() => {
        if (!showInstructions) {
            lastUpdateTimeRef.current = performance.now();
            animationRef.current = requestAnimationFrame(gameLoop);
        }
        return () => cancelAnimationFrame(animationRef.current);
    }, [showInstructions, gameLoop]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showInstructions || gameOver) return;
            e.preventDefault();
            if (e.key === 'ArrowLeft' || e.key === 'a') setKeys(prev => ({ ...prev, left: true }));
            if (e.key === 'ArrowRight' || e.key === 'd') setKeys(prev => ({ ...prev, right: true }));
            if (e.key === 'ArrowUp' || e.key === 'w' || e.key === ' ') setKeys(prev => ({ ...prev, up: true }));
            if (e.key === 'e') setKeys(prev => ({ ...prev, interact: true }));
            if (e.key === 'p') setPaused(p => !p);
            if (e.key === 'm') setMuted(m => !m);
            if (e.key === 'Escape') onSkip();
        };

        const handleKeyUp = (e) => {
            if (showInstructions || gameOver) return;
            e.preventDefault();
            if (e.key === 'ArrowLeft' || e.key === 'a') setKeys(prev => ({ ...prev, left: false }));
            if (e.key === 'ArrowRight' || e.key === 'd') setKeys(prev => ({ ...prev, right: false }));
            if (e.key === 'ArrowUp' || e.key === 'w' || e.key === ' ') setKeys(prev => ({ ...prev, up: false }));
            if (e.key === 'e') setKeys(prev => ({ ...prev, interact: false }));
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [showInstructions, gameOver, onSkip]);

    useEffect(() => {
        if (!showInstructions && !gameOver && gameAreaFocusRef.current) {
            gameAreaFocusRef.current.focus();
        }
    }, [showInstructions, gameOver]);

    return (
        <div className="portfolio-game-container">
            {showInstructions ? (
                <div className="game-instructions">
                    <h2>Interactive Portfolio Guide</h2>
                    <div className="instruction-content">
                        <div className="instruction-text">
                            <p>Welcome to my interactive portfolio tour! Explore different sections of my website through this mini-adventure.</p>
                            <ul>
                                <li>Use <strong>Arrow Keys</strong> or <strong>A/D</strong> to move</li>
                                <li>Press <strong>Space</strong> or <strong>W</strong> to jump</li>
                                <li>Press <strong>E</strong> to interact with terminals</li>
                                <li>Press <strong>ESC</strong> at any time to exit the game</li>
                                <li>Find the <strong>Data Key</strong> to unlock the overview section</li>
                                <li>You can visit any section directly from its terminal</li>
                            </ul>
                        </div>
                        <div className="character-preview">
                            <div className="character-animated">
                                <div className="player right moving" />
                            </div>
                            <p>Your Guide</p>
                        </div>
                    </div>
                    <div className="instruction-buttons">
                        <button className="start-game-btn" onClick={() => setShowInstructions(false)}>Start Tour</button>
                        <button className="skip-game-btn" onClick={onSkip}>Skip Tour</button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="game-header">
                        <div className="game-info">
                            <div>Data Points: {score}</div>
                            <div>Sections: {terminalsActivated.filter(s => ['about', 'projects', 'experience', 'contact', 'skills'].includes(s)).length}/5</div>
                        </div>
                        <div className="game-controls">
                            <button onClick={() => setPaused(p => !p)}>{paused ? 'Resume' : 'Pause'}</button>
                            <button onClick={toggleShop}>Upgrades</button>
                            <button onClick={() => setMuted(m => !m)}>{muted ? 'Unmute' : 'Mute'}</button>
                            <button onClick={onSkip}>Exit Tour</button>
                        </div>
                        {hasKey && <div className="key-icon">🔑</div>}
                    </div>
                    <div
                        className="game-area-container"
                        ref={el => {
                            gameAreaRef.current = el;
                            gameAreaFocusRef.current = el;
                        }}
                        tabIndex={0}
                    >
                        <div className="circuit-background" />
                        <div className="holo-clouds" />
                        
                        <div className="mobile-controls">
                            <button
                                className="control-btn left"
                                onTouchStart={() => handleButton('left', true)}
                                onTouchEnd={() => handleButton('left', false)}
                                onMouseDown={() => handleButton('left', true)}
                                onMouseUp={() => handleButton('left', false)}
                                onMouseLeave={() => handleButton('left', false)}
                            >
                                ◀
                            </button>
                            <button
                                className="control-btn jump"
                                onTouchStart={() => handleButton('jump', true)}
                                onTouchEnd={() => handleButton('jump', false)}
                                onMouseDown={() => handleButton('jump', true)}
                                onMouseUp={() => handleButton('jump', false)}
                                onMouseLeave={() => handleButton('jump', false)}
                            >
                                ▲
                            </button>
                            <button
                                className="control-btn right"
                                onTouchStart={() => handleButton('right', true)}
                                onTouchEnd={() => handleButton('right', false)}
                                onMouseDown={() => handleButton('right', true)}
                                onMouseUp={() => handleButton('right', false)}
                                onMouseLeave={() => handleButton('right', false)}
                            >
                                ▶
                            </button>
                            <button
                                className="control-btn interact"
                                onTouchStart={() => handleButton('interact', true)}
                                onTouchEnd={() => handleButton('interact', false)}
                                onMouseDown={() => handleButton('interact', true)}
                                onMouseUp={() => handleButton('interact', false)}
                                onMouseLeave={() => handleButton('interact', false)}
                            >
                                E
                            </button>
                        </div>

                        <div
                            className="game-area"
                            style={{ transform: `translate3d(-${cameraOffset}px, -${cameraOffsetY}px, 0)` }}
                        >
                            {DECORATIONS.map((d, i) => (
                                <div
                                    key={`decoration-${i}`}
                                    className={`decoration ${d.type}`}
                                    style={{ 
                                        left: d.x, 
                                        top: d.y,
                                        opacity: d.hidden ? 0 : 1
                                    }}
                                    data-text={d.text}
                                />
                            ))}
                            <div
                                className={`player ${player.facingRight ? 'right' : 'left'} ${player.isJumping ? 'jumping' : ''} ${player.isMoving ? 'moving' : ''}`}
                                style={{ left: player.x, top: player.y, width: player.width, height: player.height }}
                            />
                            {PLATFORMS.map((p, i) => {
                                const isMoving = p.moving;
                                const state = isMoving ? platformStates[PLATFORMS.filter(p => p.moving).indexOf(p)] : null;
                                return (
                                    <div
                                        key={`platform-${i}`}
                                        className={`platform ${p.type} ${p.type === 'fortress-gate' && terminalsActivated.includes('fortress-altar') ? 'open' : ''}`}
                                        style={{
                                            left: p.x,
                                            top: isMoving ? state?.y : p.y,
                                            width: p.width,
                                            height: p.height
                                        }}
                                    >
                                        {p.type && !['fortress-altar', 'fortress-gate'].includes(p.type) && (
                                            <div className="platform-label">
                                                {p.type.charAt(0).toUpperCase() + p.type.slice(1)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {collectibles.map((c, i) => (
                                <div
                                    key={`collectible-${i}`}
                                    className={`collectible ${c.type}`}
                                    style={{ left: c.x, top: c.y }}
                                />
                            ))}
                            {TERMINALS.map((t, i) => (
                                <div
                                    key={`terminal-${i}`}
                                    className={`terminal ${t.type}`}
                                    style={{
                                        left: t.x,
                                        top: t.y - 40,
                                        display: terminalsActivated.includes(t.type) ? 'none' : 'block',
                                        zIndex: 6,
                                        backgroundImage: `url(${terminalImage})`,
                                        backgroundSize: 'contain',
                                        backgroundRepeat: 'no-repeat'
                                    }}
                                >
                                    <div className="terminal-label">
                                        {t.type.charAt(0).toUpperCase() + t.type.slice(1)}
                                    </div>
                                    {!terminalsActivated.includes(t.type) &&
                                        player.x < t.x + 70 &&
                                        player.x + player.width > t.x - 70 &&
                                        player.y < (t.y - 40) + 40 + 80 &&
                                        player.y + player.height > (t.y - 40) - 27.5 && (
                                            <div className="interact-prompt" style={{ left: 0, top: -40 }}>
                                                E
                                            </div>
                                        )}
                                </div>
                            ))}
                            {hasKey && !terminalsActivated.includes('fortress-altar') &&
                                player.x < 5100 + 130 + 40 &&
                                player.x + player.width > 5100 - 40 &&
                                player.y < 480 + 15 + 40 &&
                                player.y + player.height > 480 - 40 && (
                                    <div className="interact-prompt" style={{ left: 5100 + (130/2) - 15, top: 480 - 50, width: 'auto', padding: '5px 10px' }}>
                                        Press E to place key
                                    </div>
                                )}
                            {terminalsActivated.includes('fortress-altar') &&
                                player.x < 5270 + 20 + 40 &&
                                player.x + player.width > 5270 - 40 &&
                                player.y < 432 + 48 + 40 &&
                                player.y + player.height > 432 - 40 && (
                                    <div className="interact-prompt" style={{ left: 5270 + (20/2) - 15, top: 432 - 35 }}>
                                        E
                                    </div>
                                )}
                            {particles.map(particle => (
                                <div 
                                    key={particle.id}
                                    className="particle"
                                    style={{
                                        position: 'absolute',
                                        left: particle.x,
                                        top: particle.y,
                                        width: '10px',
                                        height: '10px',
                                        background: 'rgba(0, 255, 255, 0.8)',
                                        borderRadius: '50%',
                                        transform: `scale(${1 - particle.progress})`,
                                        zIndex: 5
                                    }}
                                />
                            ))}
                            {upgrades.includes('blue_trail') && player.isMoving && (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <div 
                                        key={`trail-${i}`}
                                        className="player-effect blue-trail"
                                        style={{ 
                                            left: player.facingRight ? player.x - 10 - (i * 8) : player.x + player.width - 10 + (i * 8),
                                            top: player.y + 15 + (Math.random() * 10 - 5),
                                            width: 25 - (i * 5),
                                            height: 25 - (i * 5),
                                            opacity: 1 - (i * 0.2),
                                            zIndex: 9
                                        }}
                                    />
                                ))
                            )}
                            {upgrades.includes('golden_glow') && (
                                <div 
                                    className="player-effect golden-aura"
                                    style={{ 
                                        left: player.x - 15, 
                                        top: player.y - 15,
                                        width: player.width + 30,
                                        height: player.height + 30,
                                        zIndex: 8
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    {paused && (
                        <div className="game-paused">
                            <h3>Tour Paused</h3>
                            <button onClick={() => setPaused(false)}>Resume</button>
                            <button onClick={onSkip}>Exit Tour</button>
                        </div>
                    )}
                    {gameOver && (
                        <div className="game-complete">
                            <h2>Tour Complete</h2>
                            <p>You've accessed all portfolio sections!</p>
                            <button onClick={onSkip}>View Portfolio</button>
                        </div>
                    )}
                    {sectionInfo && (
                        <div className="section-overlay">
                            <h2>{sectionInfo.title}</h2>
                            <p>{sectionInfo.description}</p>
                            <div className="section-buttons">
                                {!gameOver && <button onClick={() => setSectionInfo(null)}>Continue Tour</button>}
                                {sectionInfo.visitPath && (
                                    <button 
                                        className="visit-now-btn"
                                        onClick={() => handleVisit(sectionInfo.visitPath)}
                                    >
                                        {sectionInfo.visitText || 'Visit Now'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {showShop && (
                        <div className="shop-overlay">
                            <h2>Character Upgrades</h2>
                            <p>Spend your data points to enhance your character</p>
                            <p className="shop-points">Available Points: {score}</p>
                            
                            <div className="shop-items">
                                {CHARACTER_UPGRADES.map(upgrade => {
                                    const isOwned = upgrades.includes(upgrade.id);
                                    const canAfford = score >= upgrade.cost;
                                    
                                    return (
                                        <div key={upgrade.id} className={`shop-item ${isOwned ? 'owned' : ''} ${!isOwned && !canAfford ? 'cannot-afford' : ''}`}>
                                            <h3>{upgrade.name}</h3>
                                            <p>{upgrade.description}</p>
                                            {!isOwned ? (
                                                <button 
                                                    disabled={!canAfford}
                                                    onClick={() => purchaseUpgrade(upgrade.id)}
                                                >
                                                    Purchase ({upgrade.cost} points)
                                                </button>
                                            ) : (
                                                <span className="owned-label">Purchased</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            
                            <div className="shop-buttons">
                                <button onClick={() => {setShowShop(false); setPaused(false);}}>Close Shop</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PortfolioGame;